import { Controller } from "@hotwired/stimulus"

/**
 * Image Status Controller
 * 
 * Polls the backend to check when DICOM/TIFF images have been converted to JPEG.
 * Shows a loading overlay while conversion is in progress, then updates the preview
 * when the converted image is ready.
 * 
 * Connects to data-controller="image-status"
 */
export default class extends Controller {
  static targets = ["pictureOne", "pictureTwo", "pictureThree", "calibrationScreenshot"]
  
  static values = {
    url: String,                                   // Endpoint URL for status polling
    pollingInterval: { type: Number, default: 2000 }, // Poll every 2 seconds
    maxAttempts: { type: Number, default: 30 }     // Stop after 60 seconds (30 * 2s)
  }

  connect() {
    // Track which fields are being polled
    this.pollingFields = new Set()

    // Track polling intervals
    this.pollIntervals = {}

    // Track attempt counts per field
    this.attemptCounts = {}

    // Listen for DICOM upload events from image-upload controller
    // The event is now dispatched AFTER save completes, so we can start polling immediately
    this.element.addEventListener('image-upload:dicom-upload', (event) => {
      const { fieldName } = event.detail

      // Small delay to let after_commit callback start processing
      setTimeout(() => {
        this.startPolling(fieldName)
      }, 1000)
    })
  }

  disconnect() {
    // Clear all polling intervals
    Object.keys(this.pollIntervals).forEach(fieldName => {
      this.stopPolling(fieldName)
    })

    // Clear tracking data
    this.pollingFields.clear()
    this.pollIntervals = {}
    this.attemptCounts = {}
  }

  /**
   * Start polling for a specific field
   * @param {string} fieldName - Name of the field (e.g., "picture_one")
   */
  startPolling(fieldName) {
    // Validate field name
    if (!this.isValidField(fieldName)) {
      return
    }

    // Don't start if already polling this field
    if (this.pollingFields.has(fieldName)) {
      return
    }

    // Add to polling set
    this.pollingFields.add(fieldName)

    // Reset attempt counter
    this.attemptCounts[fieldName] = 0

    // Show loading overlay immediately
    this.showLoading(fieldName)

    // Start polling interval
    this.pollIntervals[fieldName] = setInterval(() => {
      this.poll(fieldName)
    }, this.pollingIntervalValue)

    // Also poll immediately
    this.poll(fieldName)
  }

  /**
   * Stop polling for a specific field
   * @param {string} fieldName - Name of the field
   */
  stopPolling(fieldName) {
    if (!this.pollingFields.has(fieldName)) {
      return
    }

    // Clear interval
    if (this.pollIntervals[fieldName]) {
      clearInterval(this.pollIntervals[fieldName])
      delete this.pollIntervals[fieldName]
    }

    // Remove from polling set
    this.pollingFields.delete(fieldName)

    // Clear attempt counter
    delete this.attemptCounts[fieldName]

    // Hide loading overlay
    this.hideLoading(fieldName)
  }

  /**
   * Poll the backend endpoint for image status
   * @param {string} fieldName - Name of the field being polled
   */
  async poll(fieldName) {
    // Check if we've exceeded max attempts
    this.attemptCounts[fieldName] = (this.attemptCounts[fieldName] || 0) + 1

    if (this.attemptCounts[fieldName] > this.maxAttemptsValue) {
      this.stopPolling(fieldName)
      this.showError(fieldName, "Conversion timeout - please refresh the page")
      return
    }

    try {
      const response = await fetch(this.urlValue, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }

      const data = await response.json()

      // Check the status for this specific field
      if (data[fieldName]) {
        const fieldData = data[fieldName]

        // If variable is true, conversion is complete
        if (fieldData.variable === true && fieldData.url) {
          this.updatePreview(fieldName, fieldData)
          this.stopPolling(fieldName)
        } else if (fieldData.attached === false) {
          // Not attached yet - after_commit might still be processing
          // Keep polling for a few more attempts
          if (this.attemptCounts[fieldName] >= 10) {
            this.showError(fieldName, "Upload may have failed - please try again")
            this.stopPolling(fieldName)
          }
        }
        // Otherwise keep polling
      }
    } catch (error) {
      console.error(`[image-status] Polling error for ${fieldName}:`, error)

      // Only show error and stop on repeated failures
      if (this.attemptCounts[fieldName] >= 5) {
        this.showError(fieldName, "Error checking conversion status")
        this.stopPolling(fieldName)
      }
    }
  }

  /**
   * Update the preview with the converted image
   * @param {string} fieldName - Name of the field
   * @param {Object} data - Status data from backend
   */
  updatePreview(fieldName, data) {
    const previewContainer = this.findPreviewContainer(fieldName)
    if (!previewContainer) {
      return
    }

    // Hide loading overlay first
    this.hideLoading(fieldName)

    // Hide the DICOM/conversion warning box
    this.hideWarningBox(fieldName, previewContainer)

    // Create new image element with the converted image - match the style of existing previews
    const imageHtml = `
      <div class="mb-2">
        <img src="${data.url}" class="img-thumbnail" alt="Preview">
      </div>
      <button type="button" class="btn btn-danger btn-sm" data-action="click->image-upload#removeImage" data-field-name="${fieldName}">
        <i class="fa fa-trash"></i> Remove Image
      </button>
    `

    previewContainer.innerHTML = imageHtml
    previewContainer.classList.remove('d-none')
  }

  /**
   * Show loading overlay on preview container
   * @param {string} fieldName - Name of the field
   */
  showLoading(fieldName) {
    const previewContainer = this.findPreviewContainer(fieldName)
    if (!previewContainer) {
      return
    }

    // Add loading class
    previewContainer.classList.add('image-preview-loading')

    // Add spinner if not already present
    if (!previewContainer.querySelector('.spinner-border')) {
      const spinner = document.createElement('div')
      spinner.className = 'spinner-border text-primary'
      spinner.setAttribute('role', 'status')
      spinner.innerHTML = '<span class="sr-only">Converting...</span>'
      previewContainer.appendChild(spinner)
    }
  }

  /**
   * Hide loading overlay from preview container
   * @param {string} fieldName - Name of the field
   */
  hideLoading(fieldName) {
    const previewContainer = this.findPreviewContainer(fieldName)
    if (!previewContainer) {
      return
    }

    // Remove loading class
    previewContainer.classList.remove('image-preview-loading')

    // Remove spinner
    const spinner = previewContainer.querySelector('.spinner-border')
    if (spinner) {
      spinner.remove()
    }
  }

  /**
   * Hide the DICOM/TIFF conversion warning box after successful conversion
   * @param {string} fieldName - Name of the field
   * @param {HTMLElement} previewContainer - The preview container element
   */
  hideWarningBox(fieldName, previewContainer) {
    // Find the warning container - it's a sibling of the preview container
    // Look for the alert-warning that contains DICOM/TIFF conversion message
    const columnContainer = previewContainer.closest('.col-md-4, .col-sm-6, .form-group')
    if (!columnContainer) {
      return
    }

    // Find warning containers with conversion messages
    const warningContainers = columnContainer.querySelectorAll('[data-image-upload-target="warning"], .alert-warning')
    warningContainers.forEach(warning => {
      if (warning.textContent.includes('DICOM') ||
          warning.textContent.includes('TIFF') ||
          warning.textContent.includes('converted') ||
          warning.textContent.includes('compressed')) {
        warning.classList.add('d-none')
        warning.innerHTML = ''
      }
    })
  }

  /**
   * Show error message in preview container
   * @param {string} fieldName - Name of the field
   * @param {string} message - Error message to display
   */
  showError(fieldName, message) {
    const previewContainer = this.findPreviewContainer(fieldName)
    if (!previewContainer) {
      return
    }

    this.hideLoading(fieldName)

    const errorHtml = `
      <div class="alert alert-warning alert-sm" role="alert">
        <i class="fas fa-exclamation-triangle"></i> ${message}
      </div>
    `

    previewContainer.innerHTML = errorHtml
  }

  /**
   * Find the preview container for a given field name
   * @param {string} fieldName - Name of the field (e.g., "picture_one")
   * @returns {HTMLElement|null} The preview container element
   */
  findPreviewContainer(fieldName) {
    // Convert snake_case to camelCase for target names
    const targetName = this.snakeToCamel(fieldName)

    // Check if we have this target
    if (!this.hasTarget(targetName)) {
      return null
    }

    return this[`${targetName}Target`]
  }

  /**
   * Check if a field name is valid
   * @param {string} fieldName - Name to validate
   * @returns {boolean} True if valid
   */
  isValidField(fieldName) {
    const validFields = ['picture_one', 'picture_two', 'picture_three', 'calibration_screenshot']
    return validFields.includes(fieldName)
  }

  /**
   * Convert snake_case to camelCase
   * @param {string} str - Snake case string
   * @returns {string} Camel case string
   */
  snakeToCamel(str) {
    return str.replace(/_([a-z])/g, (match, letter) => letter.toUpperCase())
  }

  /**
   * Check if a target exists
   * @param {string} targetName - Name of the target in camelCase
   * @returns {boolean} True if target exists
   */
  hasTarget(targetName) {
    return this.constructor.targets.includes(targetName)
  }
}
